// Bileli · modules/live/model — shared «what's LIVE right now» state (zustand VANILLA + Teact adapter).
// One polling loop feeds every surface (Dashboard banner, Мои курсы banner, LearningCard pips) instead
// of each component firing its own one-shot GET /me/live: subscribers acquire/release the loop, the
// first one starts it, the last one stops it. Polls every 60s while the tab is visible (a teacher
// pressing «В эфир» becomes visible to students within a minute — the backend's websocket broadcast
// isn't in the openapi contract, so polling is the frontend-only option).

import { getGlobal } from '../../../../global';

import type { LiveSession } from './live.api';

import { createStore } from '../../../shared/lib/zustandTeact';
import { fetchMyLive } from './live.api';

export interface MyLiveState {
  sessions: LiveSession[];
  /** 0 until the first successful fetch — lets views distinguish «loading» from «nothing live». */
  fetchedAt: number;
}

export const myLiveStore = createStore<MyLiveState>(() => ({ sessions: [], fetchedAt: 0 }));

const POLL_MS = 60_000;

let pollTimer: number | undefined;
let subscriberCount = 0;
let inFlight = false;
let trailingQueued = false;

async function refresh(): Promise<void> {
  // No Bileli session (anon catalog browsing) — nothing personal to poll; keep the last state.
  if (!getGlobal().bileliAuth?.isAuthed) return;
  if (inFlight) {
    // A poke landing mid-flight used to be DROPPED — the in-flight response predates the action that
    // poked, so post-action state could lag a full 60s tick (Fable review LOW-a). Queue ONE re-run.
    trailingQueued = true;
    return;
  }
  inFlight = true;
  try {
    const sessions = await fetchMyLive();
    myLiveStore.setState({ sessions, fetchedAt: Date.now() });
  } catch {
    // transient failure — keep showing the last known state, next tick retries
  } finally {
    inFlight = false;
    if (trailingQueued) {
      trailingQueued = false;
      void refresh();
    }
  }
}

function onVisible(): void {
  // Waking the tab refreshes immediately when the last poll is stale (interval ticks were skipped).
  if (!document.hidden && Date.now() - myLiveStore.getState().fetchedAt > POLL_MS) void refresh();
}

/** Start (or join) the shared polling loop; returns the release fn — call it on unmount. */
export function acquireMyLive(): () => void {
  subscriberCount += 1;
  if (subscriberCount === 1) {
    void refresh();
    pollTimer = window.setInterval(() => {
      if (!document.hidden) void refresh();
    }, POLL_MS);
    document.addEventListener('visibilitychange', onVisible);
  }
  return () => {
    subscriberCount -= 1;
    if (subscriberCount === 0) {
      if (pollTimer !== undefined) window.clearInterval(pollTimer);
      pollTimer = undefined;
      document.removeEventListener('visibilitychange', onVisible);
    }
  };
}

/** Refresh soon after a state-changing action (e.g. the teacher pressed «В эфир» in this session). */
export function pokeMyLive(): void {
  void refresh();
}
